import { type MouseEvent, useCallback, useMemo } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

type StepDirection = 'prev' | 'next';

const STEP_LABELS = {
  prev: 'Prev',
  next: 'Next',
} as const satisfies Record<StepDirection, string>;

const STEP_ICONS = {
  prev: IconChevronLeft,
  next: IconChevronRight,
} as const satisfies Record<StepDirection, typeof IconChevronLeft>;

// atoms merges Link's own classes by concatenation rather than through
// tailwind-merge, so plain `no-underline` cannot win.
const STEP_CLASSES = 'flex items-center gap-1 no-underline!';
const PAGE_CLASSES =
  'flex size-10 items-center justify-center rounded-lg border text-body-sm no-underline!';

export type PaginationItem = { kind: 'page'; page: number } | { kind: 'gap'; after: number };

/**
 * Inventory shrinks, so a page URL that was valid when it was indexed goes out
 * of range routinely. Clamping keeps the nav pointing back inside the range
 * instead of offering a step further past the end as its only live control.
 */
const clampPage = (page: number, totalPages: number): number =>
  Math.min(Math.max(page, 1), totalPages);

const NEIGHBOUR_PAGES = 2;

/**
 * `1 … current-2 current current+2 … last`, collapsing only runs worth
 * collapsing: a single hidden page is rendered instead of an ellipsis that
 * would stand in for it. Contiguous windows need no ellipsis at all, so a
 * small market lists every page without a special case.
 */
export const paginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  const page = clampPage(currentPage, totalPages);
  const neighbours = Array.from(
    { length: NEIGHBOUR_PAGES * 2 + 1 },
    (_, index) => page - NEIGHBOUR_PAGES + index,
  );
  const anchors = [1, ...neighbours, totalPages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const items: PaginationItem[] = [];
  let previous = 0;
  for (const page of anchors) {
    if (page === previous) {
      continue;
    }
    const missing = page - previous - 1;
    if (previous !== 0 && missing === 1) {
      items.push({ kind: 'page', page: page - 1 });
    } else if (missing > 1) {
      items.push({ kind: 'gap', after: previous });
    }
    items.push({ kind: 'page', page });
    previous = page;
  }
  return items;
};

// A modified or non-primary click means the visitor asked the browser for the
// href — a new tab, a download, a bookmark — so in-place paging must stand down.
const isPlainClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

interface PageLinkProps {
  page: number;
  analyticsName: string;
  className: string;
  children: React.ReactNode;
  ariaLabel?: string;
  hrefForPage: (page: number) => string;
  onPageSelect: (page: number) => void;
}

const PageLink = ({
  page,
  analyticsName,
  className,
  children,
  ariaLabel,
  hrefForPage,
  onPageSelect,
}: PageLinkProps) => {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isPlainClick(event)) {
        event.preventDefault();
        onPageSelect(page);
      }
    },
    [onPageSelect, page],
  );

  return (
    <Link
      analyticsName={analyticsName}
      href={hrefForPage(page)}
      aria-label={ariaLabel}
      className={className}
      onClick={handleClick}
    >
      {children}
    </Link>
  );
};

interface PaginationSlotProps {
  item: PaginationItem;
  currentPage: number;
  hrefForPage: (page: number) => string;
  onPageSelect: (page: number) => void;
}

const PaginationSlot = ({ item, currentPage, hrefForPage, onPageSelect }: PaginationSlotProps) => {
  if (item.kind === 'gap') {
    return (
      <Box
        aria-hidden
        className="flex size-10 items-center justify-center text-od-content-tertiary"
      >
        …
      </Box>
    );
  }

  // The current page is the destination, so it is marked rather than linked.
  if (item.page === currentPage) {
    return (
      <Box
        aria-current="page"
        aria-label={`Page ${item.page}`}
        className={cn(
          PAGE_CLASSES,
          'border-od-content-primary font-medium text-od-content-primary',
        )}
      >
        {item.page}
      </Box>
    );
  }

  return (
    <PageLink
      page={item.page}
      analyticsName="marketplace-pagination-page"
      ariaLabel={`Page ${item.page}`}
      className={cn(
        PAGE_CLASSES,
        'border-od-border-primary text-od-content-primary hover:border-od-content-primary',
      )}
      hrefForPage={hrefForPage}
      onPageSelect={onPageSelect}
    >
      {item.page}
    </PageLink>
  );
};

interface PaginationActionsProps {
  /** One-based page currently rendered, matching the URL. */
  currentPage: number;
  /** Pages available for the active search. */
  totalPages: number;
  /** Canonical href for a one-based page. */
  hrefForPage: (page: number) => string;
  /** Applies a page in place, bypassing a document navigation. */
  onPageSelect: (page: number) => void;
  isLoading?: boolean;
}

export const PaginationActions = ({
  currentPage,
  totalPages,
  hrefForPage,
  onPageSelect,
  isLoading,
}: PaginationActionsProps) => {
  const page = clampPage(currentPage, totalPages);
  const items = useMemo(() => paginationItems(page, totalPages), [page, totalPages]);

  if (totalPages < 2) {
    return null;
  }

  const step = (direction: StepDirection) => {
    const Icon = STEP_ICONS[direction];
    const targetPage = direction === 'prev' ? page - 1 : page + 1;
    const isUnavailable = direction === 'prev' ? page <= 1 : page >= totalPages;
    const content = (
      <>
        {direction === 'prev' && <Icon aria-hidden size={18} />}
        <Text as="span" className="text-body-sm">
          {STEP_LABELS[direction]}
        </Text>
        {direction === 'next' && <Icon aria-hidden size={18} />}
      </>
    );

    // Kept visible for layout stability at the ends of the range, and hidden
    // from assistive tech because a boundary step conveys nothing an absent
    // link doesn't.
    if (isUnavailable) {
      return (
        <Box aria-hidden className={cn(STEP_CLASSES, 'text-od-content-tertiary opacity-40')}>
          {content}
        </Box>
      );
    }
    return (
      <PageLink
        page={targetPage}
        analyticsName={`marketplace-pagination-${direction}`}
        className={cn(STEP_CLASSES, 'text-od-content-primary')}
        hrefForPage={hrefForPage}
        onPageSelect={onPageSelect}
      >
        {content}
      </PageLink>
    );
  };

  return (
    <nav aria-label="Listing pages" aria-busy={isLoading || undefined} className="pt-10 pb-8">
      {/* Spacing, not width, separates the three parts: the inner gap sets the
          rhythm between the pages and the outer one keeps the steps clear of
          the run, so the group stays together however wide the column is. */}
      <Row className="items-center justify-center gap-6">
        {step('prev')}
        <Row className="min-w-0 flex-wrap items-center justify-center gap-2">
          {items.map((item) => (
            <PaginationSlot
              key={item.kind === 'gap' ? `gap-${item.after}` : item.page}
              item={item}
              currentPage={page}
              hrefForPage={hrefForPage}
              onPageSelect={onPageSelect}
            />
          ))}
        </Row>
        {step('next')}
      </Row>
    </nav>
  );
};
